import debounce from 'lodash/debounce';
import LocalStorageConstants from '~/constants/LocalStorageConstants';
import rollbar from '~/vendor/rollbar';

const __TEST__ = import.meta.env.MODE === 'test';
const __DISABLE_LOCAL_STORAGE__ = import.meta.env.NDX_DISABLE_LOCAL_STORAGE;

export default function persistInLocalStorage(customConfig = {}) {
  const config = {
    key: 'snapshot',
    timestamp: Date.now(),
    versionKey: 'version',
    version: null,
    deserialize: data => data,
    serialize: data => data,
    readError: error =>
      rollbar.error(
        'Failed to retrieve initial state from localStorage:',
        error
      ),
    writeError: error =>
      rollbar.error('Unable to persist state to localStorage:', error),
    isValidData: () => true,
    blackListedTypes: [],
    writeDelay: 3000,
    ...customConfig
  };

  const write = debounce((action, store) => {
    const state = store.getState();
    const serializedState = JSON.stringify(config.serialize(state));
    try {
      if (config.blackListedTypes.indexOf(action.type) === -1) {
        localStorage.setItem(config.key, serializedState);
        localStorage.setItem(config.versionKey, config.version);
        localStorage.setItem(LocalStorageConstants.TIMESTAMP, config.timestamp);
      }
    } catch (e) {
      config.writeError(e);
    }
  }, config.writeDelay);

  window.addEventListener('pagehide', () => write.flush());

  return next => (reducer, initialState) => {
    let finalInitialState = initialState;

    try {
      const localStorageData = localStorage.getItem(config.key);
      if (localStorageData) {
        const deserializedState = config.deserialize(
          JSON.parse(localStorageData)
        );
        if (config.isValidData(config.version, deserializedState)) {
          finalInitialState = deserializedState;
        }
      }
    } catch (e) {
      config.readError(e);
    }

    const store = next(reducer, finalInitialState);

    return {
      ...store,
      dispatch(action) {
        store.dispatch(action);
        write(action, store);
        return action;
      }
    };
  };
}

export function localStorageAvailable() {
  if (__TEST__ || __DISABLE_LOCAL_STORAGE__) {
    return false;
  }
  try {
    const storage = window.localStorage;
    const testItem = '__storage_test__';
    storage.setItem(testItem, testItem);
    storage.removeItem(testItem);
    return true;
    // eslint-disable-next-line no-unused-vars
  } catch (e) {
    return false;
  }
}
